import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  User,
  Bell,
  Lock,
  Palette,
  HelpCircle,
  Info,
  LogOut,
  ChevronRight,
  ChevronLeft,
  Mail,
  Shield,
  Database,
  Trash2,
  Download,
  Moon,
  Sun,
  Globe,
  CreditCard,
  FileText,
  Settings as SettingsIcon,
} from "lucide-react";
import { logoutUser } from "../../redux/slices/authSlice";

const MobileProfileNavigation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const settingsSections = [
    {
      title: "Account",
      icon: User,
      items: [
        {
          label: "Profile",
          icon: User,
          onClick: () => navigate("/profile"),
          description: "Manage your profile information",
        },
        {
          label: "Email",
          icon: Mail,
          value: user?.email || "Not set",
          description: "Your registered email address",
        },
      ],
    },
    {
      title: "App Features",
      icon: SettingsIcon,
      items: [
        {
          label: "Transactions",
          icon: CreditCard,
          onClick: () => navigate("/transactions"),
          description: "View all transactions",
        },
        {
          label: "Tasks",
          icon: FileText,
          onClick: () => navigate("/tasks"),
          description: "Manage your tasks",
        },
        {
          label: "Passwords",
          icon: Lock,
          onClick: () => navigate("/passwords"),
          description: "Password manager",
        },
        {
          label: "Budget",
          icon: Database,
          onClick: () => navigate("/budget"),
          description: "Budget and bills",
        },
      ],
    },
    {
      title: "Preferences",
      icon: Palette,
      items: [
        {
          label: "Notifications",
          icon: Bell,
          onClick: () => navigate("/settings/notifications"),
          description: "Manage notification settings",
        },
        {
          label: "Language",
          icon: Globe,
          onClick: () => navigate("/settings/language"),
          description: "App language",
        },
        {
          label: "Theme",
          icon: Moon,
          onClick: () => navigate("/settings/theme"),
          description: "Appearance settings",
        },
      ],
    },
    {
      title: "Privacy & Security",
      icon: Shield,
      items: [
        {
          label: "Privacy Policy",
          icon: Shield,
          onClick: () => navigate("/settings/privacy"),
          description: "Read our privacy policy",
        },
        {
          label: "Terms of Service",
          icon: FileText,
          onClick: () => navigate("/settings/terms"),
          description: "View terms and conditions",
        },
        {
          label: "Data Export",
          icon: Download,
          onClick: () => navigate("/settings/data-export"),
          description: "Download your data",
        },
      ],
    },
    {
      title: "Support",
      icon: HelpCircle,
      items: [
        {
          label: "Help Center",
          icon: HelpCircle,
          onClick: () => navigate("/settings/help"),
          description: "Get help and support",
        },
        {
          label: "About",
          icon: Info,
          onClick: () => navigate("/settings/about"),
          description: "App version and info",
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      

      {/* Content */}
      <div className="px-6 py-10 space-y-4">
        {/* User Info Card */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-black text-slate-900">
                {user?.displayName || "User"}
              </h3>
              <p className="text-sm text-slate-500 font-semibold">
                {user?.email || "No email"}
              </p>
            </div>
            <button
              onClick={() => navigate("/profile")}
              className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Settings Sections */}
        {settingsSections.map((section, sectionIndex) => (
          <div
            key={sectionIndex}
            className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden"
          >
            {/* Section Header */}
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <section.icon className="w-4 h-4 text-slate-600" />
                <h2 className="text-xs font-black text-slate-600 uppercase tracking-widest">
                  {section.title}
                </h2>
              </div>
            </div>

            {/* Section Items */}
            <div>
              {section.items.map((item, itemIndex) => (
                <button
                  key={itemIndex}
                  onClick={item.onClick}
                  disabled={!item.onClick}
                  className={`w-full px-5 py-4 flex items-center gap-4 ${
                    itemIndex !== section.items.length - 1
                      ? "border-b border-slate-100"
                      : ""
                  } ${
                    item.onClick
                      ? "hover:bg-slate-50 cursor-pointer"
                      : "cursor-default"
                  } transition-colors`}
                >
                  <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-black text-slate-900">
                      {item.label}
                    </p>
                    {item.description && (
                      <p className="text-xs text-slate-500 font-semibold mt-0.5">
                        {item.description}
                      </p>
                    )}
                  </div>
                  {item.value && (
                    <span className="text-sm text-slate-500 font-semibold">
                      {item.value}
                    </span>
                  )}
                  {item.onClick && (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="w-full bg-white rounded-2xl p-5 shadow-sm border border-red-200 hover:bg-red-50 transition-all group"
        >
          <div className="flex items-center justify-center gap-3">
            <LogOut className="w-5 h-5 text-red-600" />
            <span className="text-base font-black text-red-600 uppercase tracking-widest">
              Logout
            </span>
          </div>
        </button>

        {/* App Version */}
        <div className="text-center py-4">
          <p className="text-xs text-slate-400 font-semibold">
            M-Tracker v1.0.0
          </p>
          <p className="text-xs text-slate-400 font-semibold mt-1">
            © 2025 All rights reserved
          </p>
        </div>
      </div>
    </div>
  );
};

export default MobileProfileNavigation;
